import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, IconButton, MenuItem, Select, Stack, Tooltip, Typography, Button } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from '@mui/icons-material';

import { Spinner, TwoOptionsDialog } from 'components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { NoFaxesOverlay } from '../NoFaxesOverlay/NoFaxesOverlay';
import { MoreOptions } from '../MoreOptions/MoreOptions';
import { useDialog } from 'hooks';
import { ReceivedFaxDto } from 'types/fax';
import SendFaxDialog from '../SendFaxDialog/SendFaxDialog';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFax } from 'context/fax';
import { useApiRequests } from 'hooks/useApiRequests';
import { getIntlLocale } from 'utils';

import styles from './ReceivedFaxes.module.scss';

type SortColumn = 'filename' | 'from' | 'date';

type Props = {
  onFaxSent?: () => void;
  initialRowsPerPage?: number;
};

const rowsPerPageOptions = [5, 10, 25];

/* =====================
   Component
===================== */

export function ReceivedFaxes({ onFaxSent, initialRowsPerPage }: Props) {
  const { t, i18n } = useTranslation('settings');
  const tableRef = useRef<HTMLDivElement>(null);
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const { receivedFaxes, faxNumber, loadingReceived, loadReceivedFaxes, loadSentFaxes } = useFax();

  const { downloadReceivedFax, deleteReceivedFax, sendFax } = useApiRequests();
  const { addOptimisticSentFax, removeReceivedFax, scheduleSentFaxReconciliation } = useFax();

  const [sendOpen, setSendOpen] = useState(false);
  const [selectedFaxIds, setSelectedFaxIds] = useState<string[]>([]);

  const [sortConfig, setSortConfig] = useState<{
    column: SortColumn | null;
    order: 'asc' | 'desc' | 'none';
  }>({
    column: 'date',
    order: 'desc',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage ?? 10);

  const formatReceivedDate = (epoch: number) => {
    const d = new Date(epoch);
    const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

    const month = d.toLocaleString(locale, { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12 || 12;
    const paddedHours = hours.toString().padStart(2, '0');

    return `${month} ${day}, ${year}, ${paddedHours}:${minutes} ${ampm}`;
  };

  /* =====================
     Sorting
  ===================== */

  const handleSort = (column: SortColumn) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        const nextOrder = prev.order === 'none' ? 'asc' : prev.order === 'asc' ? 'desc' : 'none';
        return { column, order: nextOrder };
      }
      return { column, order: 'asc' };
    });
  };

  const sortedFaxes = useMemo(() => {
    if (!sortConfig.column || sortConfig.order === 'none') {
      return receivedFaxes;
    }

    return [...receivedFaxes].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (sortConfig.column) {
        case 'filename':
          aVal = a.fileName.toLowerCase();
          bVal = b.fileName.toLowerCase();
          break;
        case 'from':
          aVal = a.from.toLowerCase();
          bVal = b.from.toLowerCase();
          break;
        case 'date':
          aVal = a.receivedAtEpoch.toString();
          bVal = b.receivedAtEpoch.toString();
          break;
      }

      return sortConfig.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [receivedFaxes, sortConfig]);

  /* =====================
     Pagination
  ===================== */

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pagedFaxes = sortedFaxes.slice(startIndex, endIndex);

  const paginationText = `${startIndex + 1}-${Math.min(endIndex, sortedFaxes.length)} ${t('faxCommon.of')} ${
    sortedFaxes.length
  }`;

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleFirstPageClick = () => setCurrentPage(1);
  const handlePreviousPageClick = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPageClick = () => setCurrentPage((prev) => (endIndex < sortedFaxes.length ? prev + 1 : prev));
  const handleLastPageClick = () => setCurrentPage(Math.ceil(sortedFaxes.length / rowsPerPage));

  const getArrow = (column: SortColumn) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.order === 'asc' ? '▲' : sortConfig.order === 'desc' ? '▼' : '';
  };

  useEffect(() => {
    setSelectedFaxIds((prev) => prev.filter((id) => receivedFaxes.some((fax) => fax.id === id)));
  }, [receivedFaxes]);

  const toggleFaxSelection = (faxId: string) => {
    setSelectedFaxIds((prev) => (prev.includes(faxId) ? prev.filter((id) => id !== faxId) : [...prev, faxId]));
  };

  const formatPhoneNumber = (value?: string | null) => {
    if (!value) return '—';

    const digits = value.replace(/\D/g, '');

    if (digits.length !== 10) {
      return value;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  /* =====================
     Actions (Placeholders)
  ===================== */

  const handleDownload = async (fax: ReceivedFaxDto) => {
    try {
      const blob = await downloadReceivedFax(fax.id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = fax.fileName || `fax-${fax.id}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(t('faxCommon.failedToDownloadFax'), { variant: 'error' });
    }
  };

  const handleDelete = (fax: ReceivedFaxDto) => {
    const modal = showDialog(TwoOptionsDialog, {
      title: t('faxCommon.deleteFaxUpper'),
      text: <Typography variant='body2'>{t('faxCommon.deleteFaxQuestion', { fileName: fax.fileName })}</Typography>,
      confirmText: t('faxCommon.delete'),
      onConfirm: async () => {
        try {
          modal.destroy();

          // 🔥 Optimistic UI update
          removeReceivedFax(fax.id);

          // 🔥 Backend delete (SRFax + DB)
          await deleteReceivedFax(fax.id);

          enqueueSnackbar(t('faxCommon.faxDeleted'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('faxCommon.failedToDeleteFax'), { variant: 'error' });

          // Optional safety reload if delete fails
          await loadReceivedFaxes();
        }
      },
    });
  };

  const handleBulkDelete = () => {
    const selectedFaxes = receivedFaxes.filter((fax) => selectedFaxIds.includes(fax.id));
    if (selectedFaxes.length === 0) {
      return;
    }

    const modal = showDialog(TwoOptionsDialog, {
      title: t('faxCommon.deleteFaxesUpper'),
      text: (
        <Typography variant='body2'>
          {t('receivedFaxes.deleteSelectedFaxesQuestion', { count: selectedFaxes.length })}
        </Typography>
      ),
      confirmText: t('faxCommon.delete'),
      onConfirm: async () => {
        modal.destroy();

        let succeeded = 0;
        for (const fax of selectedFaxes) {
          try {
            removeReceivedFax(fax.id);
            await deleteReceivedFax(fax.id);
            succeeded += 1;
          } catch {
            // continue; a refresh below will reconcile any optimistic mismatch
          }
        }

        await loadReceivedFaxes();
        setSelectedFaxIds([]);

        if (succeeded === selectedFaxes.length) {
          enqueueSnackbar(t('receivedFaxes.deletedFaxes', { count: succeeded }), { variant: 'success' });
        } else if (succeeded > 0) {
          enqueueSnackbar(t('receivedFaxes.deletedSomeSelectedFaxes', { succeeded, total: selectedFaxes.length }), {
            variant: 'warning',
          });
        } else {
          enqueueSnackbar(t('faxCommon.failedToDeleteSelectedFaxes'), { variant: 'error' });
        }
      },
    });
  };

  /* =====================
     Render
  ===================== */

  return (
    <div className={styles.container}>
      {loadingReceived && (
        <div className={styles.overlay}>
          <Spinner small />
        </div>
      )}

      {/* Faxes table */}
      {!loadingReceived && Array.isArray(receivedFaxes) && (
        <div className={styles.mainWrapper}>
          <div className={styles.main}>
            {/* Header */}
            <Stack className={styles.header} direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='body2'>
                {t('faxCommon.myFaxNumber')} <b>{formatPhoneNumber(faxNumber)}</b>
              </Typography>

              <Stack direction='row' spacing={1} alignItems='center'>
                <Tooltip title={t('receivedFaxes.refreshReceivedFaxes')}>
                  <span>
                    <IconButton onClick={loadReceivedFaxes} disabled={loadingReceived} size='small'>
                      <RefreshIcon className={`${styles.refreshIcon} ${loadingReceived ? styles.refreshing : ''}`} />
                    </IconButton>
                  </span>
                </Tooltip>

                {selectedFaxIds.length > 0 ? (
                  <Button
                    variant='contained'
                    color='error'
                    disabled={loadingReceived}
                    onClick={handleBulkDelete}
                    sx={{ color: '#fff' }}
                  >
                    {t('faxCommon.delete')} ({selectedFaxIds.length})
                  </Button>
                ) : (
                  <Button variant='contained' disabled={loadingReceived} onClick={() => setSendOpen(true)}>
                    {t('faxCommon.sendFax')}
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Table */}
            <div className={styles.table} ref={tableRef}>
              <Grid className={styles.tableHeader}>
                <Typography variant='subtitle2' onClick={() => handleSort('from')} className={styles.sortableHeader}>
                  {t('receivedFaxes.from')} {getArrow('from')}
                </Typography>

                <Typography
                  variant='subtitle2'
                  onClick={() => handleSort('filename')}
                  className={styles.sortableHeader}
                >
                  {t('faxCommon.fileName')} {getArrow('filename')}
                </Typography>

                <Typography variant='subtitle2' className={styles.center}>
                  {t('faxCommon.pages')}
                </Typography>

                <Typography
                  variant='subtitle2'
                  onClick={() => handleSort('date')}
                  className={`${styles.sortableHeader} ${styles.dateCell}`}
                >
                  {t('receivedFaxes.dateReceived')} {getArrow('date')}
                </Typography>

                <Typography variant='subtitle2' />
              </Grid>

              <Box>
                {pagedFaxes.length === 0 ? (
                  <NoFaxesOverlay
                    onSendFaxClick={() => {
                      setSendOpen(true);
                    }}
                  />
                ) : (
                  pagedFaxes.map((fax) => (
                    <Grid
                      key={fax.id}
                      className={`${styles.tableItem} ${selectedFaxIds.includes(fax.id) ? styles.selected : ''}`}
                      onClick={() => toggleFaxSelection(fax.id)}
                    >
                      <Typography variant='body1' noWrap>
                        {formatPhoneNumber(fax.from)}
                      </Typography>

                      <Tooltip title={fax.fileName}>
                        <Typography variant='body1' noWrap>
                          {fax.fileName}
                        </Typography>
                      </Tooltip>

                      <Typography variant='body1' className={styles.center}>
                        {fax.pages}
                      </Typography>

                      <Typography variant='body1' className={styles.dateCell}>
                        {formatReceivedDate(fax.receivedAtEpoch)}
                      </Typography>

                      <div onClick={(event) => event.stopPropagation()}>
                        <MoreOptions
                          item={fax}
                          onDownload={() => handleDownload(fax)}
                          onDelete={() => handleDelete(fax)}
                          downloadEnabled
                          deleteEnabled
                        />
                      </div>
                    </Grid>
                  ))
                )}
              </Box>
            </div>

            {/* Pagination */}
            <Stack className={styles.paginationRow}>
              <div className={styles.topRow}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography>{t('faxCommon.rowsPerPage')}</Typography>
                  <Select
                    value={rowsPerPage.toString()}
                    onChange={handleRowsPerPageChange}
                    IconComponent={ChevronRightIcon}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                <Typography>{paginationText}</Typography>
              </div>

              <Stack direction='row' spacing={1}>
                <IconButton onClick={handleFirstPageClick}>
                  <FirstPageIcon />
                </IconButton>
                <IconButton onClick={handlePreviousPageClick}>
                  <ArrowBackIcon />
                </IconButton>
                <IconButton onClick={handleNextPageClick}>
                  <ArrowForwardIcon />
                </IconButton>
                <IconButton onClick={handleLastPageClick}>
                  <LastPageIcon />
                </IconButton>
              </Stack>
            </Stack>
          </div>
        </div>
      )}
      <SendFaxDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onSend={async ({ files, faxNumber, subject }) => {
          try {
            // 1️⃣ Send to backend first
            await sendFax(files, faxNumber, subject);

            // 2️⃣ Only AFTER success + dialog close do we add optimistic row
            const tempId = `local-${Date.now()}`;

            addOptimisticSentFax({
              id: tempId,
              fileName: files.length === 1 ? files[0].name : `ZIP (${files.length} files)`,
              status: 'queued',
              statusCode: 1, // required by SentFaxDto
              sentBy: t('faxCommon.you'),
              sentTo: faxNumber,
              sentAt: t('faxCommon.justNow'),
              sentAtEpoch: Date.now(),
              pages: 0,
              failedMessage: null,
            });

            // 3️⃣ Schedule reconciliation (replace optimistic with real SRFax row)
            scheduleSentFaxReconciliation();

            // 4️⃣ Snackbar timing now matches sound + dialog close
            enqueueSnackbar(t('faxCommon.faxQueued'), { variant: 'success' });

            setSendOpen(false);
          } catch {
            enqueueSnackbar(t('faxCommon.failedToQueueFax'), { variant: 'error' });
            throw new Error();
          }
        }}
      />
    </div>
  );
}

export default ReceivedFaxes;
